import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, Users, Shield, Award, Star, CheckCircle, Stethoscope, Smile, Accessibility, Sparkles } from 'lucide-react';

const HomePage = () => {
  const [activeService, setActiveService] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const services = [
    {
      title: "Soins Généraux",
      shortTitle: "Soins Généraux",
      description: isMobile ? 
        "Consultations, caries, détartrage et soins préventifs." : 
        "Consultations, soins des caries, détartrage, traitements préventifs et curatifs pour maintenir votre santé bucco-dentaire.",
      features: ["Consultations", "Caries", "Détartrage", "Prévention"],
      icon: Stethoscope,
      color: "#0EA5E9",
      bgColor: "#F0F9FF"
    },
    {
      title: "Implantologie",
      shortTitle: "Implants",
      description: isMobile ?
        "Implants dentaires pour remplacer les dents manquantes." :
        "Remplacement des dents manquantes par des implants dentaires pour retrouver fonction et esthétique.",
      features: ["Implants unitaires", "Bridges", "Prothèses", "Chirurgie guidée"],
      icon: Award,
      color: "#059669",
      bgColor: "#ECFDF5"
    },
    {
      title: "Esthétique",
      shortTitle: "Esthétique",
      description: isMobile ?
        "Blanchiment et amélioration de votre sourire." :
        "Amélioration de l'apparence de votre sourire avec des techniques modernes et personnalisées.",
      features: ["Blanchiment", "Facettes", "Couronnes", "Orthodontie"],
      icon: Smile,
      color: "#DC2626",
      bgColor: "#FEF2F2"
    }
  ];

  const horaires = [
    { jour: "Lun", heures: "09:00 - 19:00" },
    { jour: "Mar", heures: "09:00 - 19:00" },
    { jour: "Mer", heures: "09:00 - 19:00" },
    { jour: "Jeu", heures: "09:00 - 17:00" },
    { jour: "Ven", heures: "09:00 - 19:00" },
    { jour: "Sam", heures: "Fermé" },
    { jour: "Dim", heures: "Fermé" }
  ];

  const testimonials = [
    {
      name: "Marie-Laure Vassas",
      text: isMobile ?
        "Un dentiste très professionnel et accueillant, cabinet à la pointe de la modernité. Je recommande vivement !" :
        "Un dentiste très professionnel et accueillant, tout comme son cabinet très tendance et à la pointe de la modernité. Je recommande vivement !",
      rating: 5
    },
    {
      name: "Jean-François Giannoni",
      text: isMobile ?
        "Le Dr Santelli est passionné de son métier, cela se voit sur son travail. Personnel très accueillant." :
        "On peut y aller les yeux fermés car le Dr Santelli est passionné de son métier, et cela se voit sur son travail, avec un personnel très accueillant.",
      rating: 5
    },
    {
      name: "Gilles Masteau",
      text: isMobile ?
        "Dentiste très professionnel et prévenant. Très compétent, je le recommande." :
        "Dentiste très professionnel et prévenant avec ses patients. Par ailleurs il est très compétent, je le recommande.",
      rating: 5
    },
    {
      name: "Charlotte Guillien",
      text: isMobile ?
        "Lieu agréable, médecins très professionnels. Je recommande ce cabinet." :
        "Lieu agréable, médecins très professionnels. Je recommande ce cabinet dentaire.",
      rating: 5
    }
  ];

  return (
    <div className="dental-site">
      {/* Header */}
      <header className="header-nav">
        <div className="container">
          <div className="nav-content">
            <div className="logo-section">
              <h2 className="logo-text">
                {isMobile ? "Les Chênes Verts" : "Cabinet Dentaire Les Chênes Verts"}
              </h2>
            </div>
            <nav className="nav-links">
              <a href="#services" className="nav-link">Services</a>
              <a href="#about" className="nav-link">À propos</a>
              <a href="#equipe" className="nav-link">Équipe</a>
              <a href="#contact" className="nav-link">Contact</a>
              <a href="tel:+33491931958" className="btn-primary">
                <Phone size={16} />
                Prendre RDV
              </a>
            </nav>
            {/* Mobile menu button */}
            <div className="mobile-menu-btn">
              <a href="tel:+33491931958" className="btn-primary">
                <Phone size={16} />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://customer-assets.emergentagent.com/job_dentistes-marseille/artifacts/2r0zlhpq_IMG_1393.jpeg" 
            alt="Cabinet moderne"
            className="hero-bg-image"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="heading-hero">
              Votre santé dentaire entre de bonnes mains
            </h1>
            <div className="hero-doctors">
              <p className="doctors-text">
                {isMobile ?
                  "Dr Santelli et Dr Tassy - Soins de qualité à Marseille" :
                  "Dr Santelli et Dr Tassy vous proposent des soins de qualité adaptés à vos besoins à Marseille"
                }
              </p>
            </div>
            <p className="hero-subtitle">
              {isMobile ?
                "Cabinet moderne et accueillant dans un environnement chaleureux." :
                "Le Cabinet Dentaire Les Chênes Verts vous accueille dans un environnement moderne et chaleureux."
              }
            </p>
            <div className="hero-actions">
              <a href="tel:+33491931958" className="btn-primary btn-hero">
                <Phone size={18} />
                04 91 93 19 58
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* À Propos Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">À Propos du Cabinet</h2>
            <p className="section-subtitle">
              {isMobile ? 
                "Notre histoire et nos valeurs" :
                "Découvrez l'histoire de notre cabinet et notre engagement envers vos soins"
              }
            </p>
          </div>
          
          <div className="about-content">
            <div className="about-story">
              <div className="story-card">
                <div className="story-icon">
                  <Sparkles size={32} color="#0EA5E9" />
                </div>
                <div className="story-content">
                  <h3 className="heading-3">Une Histoire de Confiance</h3>
                  <p>
                    Le Cabinet Dentaire Les Chênes Verts a été fondé avec une vision claire : 
                    offrir des soins dentaires de qualité dans un environnement moderne et bienveillant. 
                    Depuis sa création, notre cabinet s'est imposé comme une référence à Marseille grâce 
                    à l'expertise de Dr Santelli et Dr Tassy.
                  </p>
                </div>
              </div>

              <div className="story-card">
                <div className="story-icon">
                  <Award size={32} color="#059669" />
                </div>
                <div className="story-content">
                  <h3 className="heading-3">Rénovation Complète 2024</h3>
                  <p>
                    {isMobile ?
                      "Cabinet entièrement rénové avec équipements dernière génération et espaces repensés pour votre confort." :
                      "Notre cabinet vient d'être entièrement rénové en 2024 ! Nous avons investi dans des équipements de dernière génération et repensé tous nos espaces pour offrir un confort optimal à nos patients."
                    }
                  </p>
                </div>
              </div>

              <div className="story-card">
                <div className="story-icon">
                  <Accessibility size={32} color="#DC2626" />
                </div>
                <div className="story-content">
                  <h3 className="heading-3">Accessibilité PMR</h3>
                  <p>
                    {isMobile ?
                      "Cabinet accessible aux personnes à mobilité réduite. Rampe d'accès, espaces adaptés et équipements spécialisés." :
                      "Notre cabinet est entièrement accessible aux personnes à mobilité réduite. Nous disposons d'une rampe d'accès, d'espaces de circulation adaptés et d'équipements spécialisés pour accueillir tous nos patients dans les meilleures conditions."
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="about-features">
              <h3 className="heading-3">Nos Engagements</h3>
              <div className="features-grid">
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Équipements dernière génération</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Normes d'hygiène renforcées</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Accueil personnalisé</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Formation continue des praticiens</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Accessibilité complète PMR</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} color="#059669" />
                  <span>Urgences dentaires acceptées</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Nos Spécialités</h2>
            <p className="section-subtitle">
              {isMobile ? 
                "Soins dentaires adaptés à vos besoins" :
                "Une gamme complète de soins dentaires adaptés à tous vos besoins"
              }
            </p>
          </div>
          
          {isMobile ? (
            // Mobile: Cards plus intuitives avec icons plus grandes
            <div className="services-mobile-grid">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div 
                    key={index}
                    className="service-mobile-card"
                    onClick={() => setActiveService(activeService === index ? -1 : index)}
                  >
                    <div className="service-mobile-header">
                      <div 
                        className="service-mobile-icon"
                        style={{ 
                          backgroundColor: service.bgColor,
                          color: service.color 
                        }}
                      >
                        <IconComponent size={32} />
                      </div>
                      <div className="service-mobile-title">
                        <h3 className="heading-3">{service.shortTitle}</h3>
                        <p className="service-mobile-summary">
                          {service.features.slice(0, 2).join(" • ")}
                        </p>
                      </div>
                      <div className="service-mobile-expand">
                        <CheckCircle 
                          size={20} 
                          className={`expand-icon ${activeService === index ? 'expanded' : ''}`}
                        />
                      </div>
                    </div>
                    
                    {activeService === index && (
                      <div className="service-mobile-details">
                        <p className="service-description">{service.description}</p>
                        <ul className="service-features-mobile">
                          {service.features.map((feature, idx) => (
                            <li key={idx}>
                              <CheckCircle size={12} style={{ color: service.color }} />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop: Layout classique
            <div className="services-grid">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div 
                    key={index}
                    className={`service-card ${activeService === index ? 'active' : ''}`}
                    onClick={() => setActiveService(index)}
                  >
                    <div className="service-icon" style={{ 
                      backgroundColor: service.bgColor,
                      color: service.color 
                    }}>
                      <IconComponent size={24} />
                    </div>
                    <div className="service-content">
                      <h3 className="heading-3">{service.title}</h3>
                      <p className="service-description">{service.description}</p>
                      <ul className="service-features">
                        {service.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircle size={14} style={{ color: service.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Cabinet Photos Section */}
      <section className="cabinet-section">
        <div className="container">
          <div className="cabinet-content">
            <div className="cabinet-info">
              <h2 className="heading-1">{isMobile ? "Cabinet" : "Notre Cabinet"}</h2>
              <p className="section-subtitle">
                {isMobile ?
                  "Environnement moderne et équipements de pointe." :
                  "Un environnement moderne et apaisant conçu pour votre confort. Équipements de dernière génération et respect des normes d'hygiène les plus strictes."
                }
              </p>
              <div className="cabinet-features">
                <div className="cabinet-feature">
                  <Shield size={18} />
                  <span>{isMobile ? "Hygiène stricte" : "Normes d'hygiène strictes"}</span>
                </div>
                <div className="cabinet-feature">
                  <Award size={18} />
                  <span>{isMobile ? "Équipements modernes" : "Équipements modernes"}</span>
                </div>
                <div className="cabinet-feature">
                  <Star size={18} />
                  <span>{isMobile ? "Atmosphère apaisante" : "Atmosphère apaisante"}</span>
                </div>
              </div>
            </div>
            
            <div className="gallery-container">
              <div className="gallery-grid">
                <div className="gallery-item large">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dentistes-marseille/artifacts/0frdgmg7_IMG_1396.jpeg" 
                    alt="Salle de soins" 
                    className="gallery-image"
                  />
                </div>
                <div className="gallery-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dentistes-marseille/artifacts/r57kquhu_IMG_1391.jpeg" 
                    alt="Accueil" 
                    className="gallery-image"
                  />
                </div>
                <div className="gallery-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dentistes-marseille/artifacts/be2993jy_IMG_1398.jpeg" 
                    alt="Équipements" 
                    className="gallery-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="equipe" className="team-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">{isMobile ? "Équipe" : "Notre Équipe"}</h2>
            <p className="section-subtitle">
              {isMobile ? 
                "Professionnels dédiés à votre santé" :
                "Des professionnels expérimentés dédiés à votre santé dentaire"
              }
            </p>
          </div>
          
          <div className="team-grid">
            <div className="team-card">
              <div className="team-avatar">
                <Users size={32} />
              </div>
              <div className="team-info">
                <h3 className="heading-3">Dr Santelli</h3>
                <p className="team-speciality">Chirurgien-dentiste</p>
                <div className="team-tags">
                  <span>Tous soins</span>
                  <span>Esthétique</span>
                  <span>Implantologie</span>
                </div>
              </div>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <Users size={32} />
              </div>
              <div className="team-info">
                <h3 className="heading-3">Dr Tassy</h3>
                <p className="team-speciality">Chirurgien-dentiste</p>
                <div className="team-tags">
                  <span>Tous soins</span>
                  <span>Esthétique</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Témoignages</h2>
            <p className="section-subtitle">{isMobile ? "Nos patients" : "La confiance de nos patients"}</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">— {testimonial.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section with Form */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2 className="heading-1">{isMobile ? "Contact" : "Informations Pratiques"}</h2>
              
              <div className="info-grid">
                <div className="info-card">
                  <div className="info-icon">
                    <MapPin size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Adresse</h4>
                    <p className="info-text">
                      Les Chênes Verts<br />
                      48 Rue Saint-Jean du Désert<br />
                      13012 Marseille{isMobile ? '' : ', France'}
                    </p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <Phone size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Téléphone</h4>
                    <a href="tel:+33491931958" className="phone-number">
                      04 91 93 19 58
                    </a>
                  </div>
                </div>

                <div className="info-card schedule-card">
                  <div className="info-icon">
                    <Clock size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Horaires</h4>
                    <div className="schedule-list">
                      {horaires.map((horaire, index) => (
                        <div key={index} className="schedule-item">
                          <span className="day">{horaire.jour}</span>
                          <span className={`hours ${horaire.heures === 'Fermé' ? 'closed' : ''}`}>
                            {horaire.heures}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-cta-section">
              <div className="contact-cta-card">
                <h3 className="heading-2">Prenez rendez-vous</h3>
                <p className="form-description">
                  {isMobile ?
                    "Appelez-nous directement pour toute demande ou rendez-vous." :
                    "Pour toute question ou prise de rendez-vous, contactez-nous directement par téléphone. Notre équipe vous répondra rapidement."
                  }
                </p>
                <a href="tel:+33491931958" className="btn-appointment btn-appointment-large">
                  <Phone size={22} />
                  04 91 93 19 58
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <h3 className="footer-title">Cabinet Dentaire Les Chênes Verts</h3>
              <p className="footer-subtitle">Dr Santelli & Dr Tassy</p>
              <p className="footer-description">
                {isMobile ?
                  "Soins dentaires de qualité à Marseille" :
                  "Soins dentaires de qualité dans un environnement moderne et bienveillant à Marseille"
                }
              </p>
            </div>
            <div className="footer-contact">
              <h4 className="footer-section-title">Contact</h4>
              <p className="footer-address">
                Les Chênes Verts<br />
                48 Rue Saint-Jean du Désert<br />
                13012 Marseille{isMobile ? '' : ', France'}
              </p>
              <a href="tel:+33491931958" className="footer-phone">04 91 93 19 58</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Cabinet Dentaire Les Chênes Verts. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;